import { create } from "zustand";
import { devtools } from "zustand/middleware";

import { AddObjectFormData } from "@/types/addObject.types";
import {
  IAddObjectFormStore,
  IFormData,
} from "@/types/store/addObjectForm.interfaces";
import { FormStepLabel } from "@/ui/AddObject/schemas/form-steps.enum";

// Combined initial state from all stores
const initialState = {
  // From formSteps.store
  currentStep: 1,
  totalSteps: 5, // Will be updated dynamically based on dealType and user roles
  currentStepId: FormStepLabel.BASIC,
  _lastTotalStepsUpdate: 0, // Debounce tracking for setTotalSteps

  // From formData.store
  formData: {
    // dealType: DealType.PRIMARY,
    page: 1,
    limit: 12,
    region: [],
    city: [],
    district: [],
    microdistrict: [],
    street: [],
    propertyType: [],
    objectType: [],
    currency: "USD",
  } as IFormData,

  // From formSubmission.store
  isSubmitting: false,
  errors: {},
  submitSuccess: false,
  createdObjectData: null, // Store created object data (id, slug, dealType)

  // Image Upload and Processing State
  imageUploadStatuses: {},
  imageProcessingJobId: null,
  imageProcessingStatus: "idle",
  imageProcessingProgress: 0,
  imageProcessingError: null,
  imageProcessingQueue: [], // New: Initialize as empty array
  sessionTempUrls: [], // Track temp urls for current session
};

// Create unified store
export const useAddObjectFormStore = create<IAddObjectFormStore>()(
  devtools(
    (set, get) => ({
      // Initial state
      ...initialState,

      // Form Steps actions with stepperize integration
      setCurrentStep: (step: number) => {
        const { currentStep: oldStep } = get();
        if (oldStep !== step) {
          set({ currentStep: step });

          // Emit step change event for stepperize sync
          if (typeof window !== "undefined") {
            window.dispatchEvent(
              new CustomEvent("zustand-step-change", {
                detail: { step, source: "zustand" },
              }),
            );
          }
        }
      },

      setCurrentStepId: (stepId: string) => {
        const { currentStepId: oldStepId } = get();
        if (oldStepId !== stepId) {
          set({ currentStepId: stepId });

          // Emit step ID change event for stepperize sync
          if (typeof window !== "undefined") {
            window.dispatchEvent(
              new CustomEvent("zustand-stepid-change", {
                detail: { stepId, source: "zustand" },
              }),
            );
          }
        }
      },

      setTotalSteps: (steps: number) => {
        const { totalSteps: oldTotal } = get();
        if (oldTotal !== steps && steps > 0 && steps <= 10) {
          // Additional guard: only allow reasonable step counts and prevent rapid changes
          const now = Date.now();
          const lastUpdate = (get() as any)._lastTotalStepsUpdate || 0;

          if (now - lastUpdate > 100) {
            // Debounce: min 100ms between updates
            set({
              totalSteps: steps,
              _lastTotalStepsUpdate: now,
            });
          }
        }
      },

      nextStep: () => {
        const { currentStep, totalSteps } = get();
        if (currentStep < totalSteps) {
          const nextStep = currentStep + 1;
          set({ currentStep: nextStep });

          // Emit step change event for stepperize sync
          if (typeof window !== "undefined") {
            window.dispatchEvent(
              new CustomEvent("zustand-step-change", {
                detail: { step: nextStep, source: "zustand" },
              }),
            );
          }
        }
      },

      prevStep: () => {
        const { currentStep } = get();
        if (currentStep > 1) {
          const prevStep = currentStep - 1;
          set({ currentStep: prevStep });

          // Emit step change event for stepperize sync
          if (typeof window !== "undefined") {
            window.dispatchEvent(
              new CustomEvent("zustand-step-change", {
                detail: { step: prevStep, source: "zustand" },
              }),
            );
          }
        }
      },

      // Form Data actions
      updateFormData: (data: Partial<IFormData>) => {
        const { formData } = get();
        set({
          formData: {
            ...formData,
            ...data,
          },
        });
      },

      resetForm: () => {
        const { createdObjectData } = get();
        set({
          formData: initialState.formData,
          currentStep: initialState.currentStep,
          currentStepId: initialState.currentStepId,
          errors: {},
          imageUploadStatuses: {},
          imageProcessingJobId: null,
          imageProcessingStatus: "idle",
          imageProcessingProgress: 0,
          imageProcessingError: null,
          imageProcessingQueue: [],
          sessionTempUrls: [],
          createdObjectData,
          // Note: Do NOT reset submitSuccess here - it should be cleared explicitly
        });
      },

      // Form Submission actions
      submitForm: async (
        formData: AddObjectFormData,
        translationFunc?: (key: string, options?: any) => string,
      ) => {
        const { addObjectService } = await import(
          "@/services/addObject.service"
        );
        // Form data validation with optional translation function
        const { isValid, errors } = addObjectService.validateForm(
          formData,
          translationFunc,
        );

        if (!isValid) {
          set({ errors });
          return Promise.reject({
            message: "Validation failed",
            details: errors,
          });
        }

        // Clear previous errors
        set({ errors: {}, isSubmitting: true });

        try {
          // Submit form data and capture response
          const createdObject = await addObjectService.submitForm(formData);

          // Set success state
          set({
            submitSuccess: true,
            isSubmitting: false,
            createdObjectData: createdObject, // Store the created object data
            // Reset form data immediately but keep submitSuccess and step state
            formData: initialState.formData,
            errors: {},
            imageUploadStatuses: {},
            imageProcessingJobId: null,
            imageProcessingStatus: "idle",
            imageProcessingProgress: 0,
            imageProcessingError: null,
            imageProcessingQueue: [],
            sessionTempUrls: [],
          });

          // DO NOT call resetForm here - this will cause duplication and reset submitSuccess
          // Form will be cleared when user clicks button in SuccessMessage

          return Promise.resolve();
        } catch (error: any) {
          // Handle submission error
          const errorMessage = error.message || "Error submitting form";
          const errorDetails = error.details || {};

          // Set error state
          set({
            isSubmitting: false,
            errors: {
              submit: errorMessage,
              ...errorDetails,
            },
          });

          return Promise.reject(error);
        }
      },

      setErrors: (errors: Record<string, string>) => {
        set({ errors });
      },

      clearErrors: () => {
        set({ errors: {} });
      },

      clearSubmitSuccess: () => {
        set({ submitSuccess: false });
      },

      // Image Upload and Processing Actions
      setImageUploadStatus: (photoId, status, data) => {
        set((state) => ({
          imageUploadStatuses: {
            ...state.imageUploadStatuses,
            [photoId]: { status, ...data },
          },
        }));
      },
      setImageProcessingJobId: (jobId) => {
        set({ imageProcessingJobId: jobId });
      },
      setImageProcessingStatus: (status) => {
        set({ imageProcessingStatus: status });
      },
      setImageProcessingProgress: (progress) => {
        set({ imageProcessingProgress: progress });
      },
      setImageProcessingError: (error) => {
        set({ imageProcessingError: error });
      },
      addImageToProcessingQueue: (url) => {
        set((state) => ({
          imageProcessingQueue: [...state.imageProcessingQueue, url],
        }));
      },
      addSessionTempUrl: (url) => {
        set((state) => ({
          sessionTempUrls: [...state.sessionTempUrls, url],
        }));
      },
      clearSessionTempUrls: () => {
        set({ sessionTempUrls: [] });
      },
      clearImageProcessingQueue: () => {
        set({ imageProcessingQueue: [] });
      },
      resetImageUploadState: () => {
        set({
          imageUploadStatuses: {},
          imageProcessingJobId: null,
          imageProcessingStatus: "idle",
          imageProcessingProgress: 0,
          imageProcessingError: null,
          imageProcessingQueue: [],
          sessionTempUrls: [],
        });
      },
    }),
    { name: "add-object-form-store" },
  ),
);
